import React, { useEffect, useState } from "react";

/**
 * Slim top-of-page reading-progress bar. Only renders when the page has
 * something worth scrolling (>1.5x viewport height). Uses scroll-aware
 * RAF batching so it does not thrash on long articles.
 */
export default function ReadingProgress() {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    let raf = 0;
    const compute = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      if (total <= 0) {
        setPct(0);
        return;
      }
      const scrolled = window.scrollY || doc.scrollTop;
      setPct(Math.min(100, Math.max(0, (scrolled / total) * 100)));
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Hide on short pages — measured at first compute. Keep it simple:
  // never show below 4% when there's barely any scroll height.
  return (
    <div
      data-testid="reading-progress"
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 h-[3px] z-[100] pointer-events-none"
    >
      <div
        className="h-full bg-clay transition-[width] duration-[80ms] ease-linear"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
